import { create } from 'zustand';
import { validateFileSize } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';
import type { TranslatableRecordFields } from '@/types/translation';

export type ProgramCardTab =
  | 'collective-impact'
  | 'our-programs'
  | 'funding-grants'
  | 'facilitating-solutions';

export type ProgramCardColor = 'light' | 'cream' | 'sage';

export interface ProgramCardPhoto {
  url: string;
  formats?: {
    thumbnail?: { url: string };
    small?: { url: string };
  };
}

export interface ProgramCard extends TranslatableRecordFields {
  id: number;
  documentId: string;
  title: string;
  description: string;
  /** Auto-translated Spanish siblings (MWBI-900). Absent until the translator runs. */
  titleEs?: string | null;
  descriptionEs?: string | null;
  tab: ProgramCardTab;
  cardColor?: ProgramCardColor | null;
  photo?: ProgramCardPhoto | null;
  createdAt: string;
}

interface ProgramCardStore {
  programCards: ProgramCard[];
  loading: boolean;
  fetchProgramCards: () => Promise<void>;
  createProgramCard: (data: {
    title: string;
    description: string;
    tab: ProgramCardTab;
    cardColor: ProgramCardColor;
    photo?: File | null;
  }) => Promise<ProgramCard | null>;
  updateProgramCard: (documentId: string, data: {
    /**
     * MWBI-900. Only the Spanish fields the admin actually EDITED, plus an optional
     * translationLocked. Echoing back untouched Spanish would diverge the stored
     * fingerprint and silently latch the item to manual for good.
     */
    translationFields?: Record<string, string | boolean>;
    title: string;
    description: string;
    tab: ProgramCardTab;
    cardColor: ProgramCardColor;
    /** undefined = keep existing | null = clear photo | File = upload new */
    photo?: File | null | undefined;
  }) => Promise<ProgramCard | null>;
  deleteProgramCard: (documentId: string) => Promise<boolean>;
}

const baseUrl = '/api/strapi';

async function uploadPhoto(file: File): Promise<number | null> {
  validateFileSize(file);
  const formData = new FormData();
  formData.append('files', file);
  const res = await fetch(`${baseUrl}/upload`, { method: 'POST', body: formData });
  if (!res.ok) return null;
  const files = await res.json();
  return files[0]?.id ?? null;
}

export const useProgramCardStore = create<ProgramCardStore>((set, get) => ({
  programCards: [],
  loading: false,

  fetchProgramCards: async () => {
    set({ loading: true });
    try {
      const programCards = await fetchStrapiCollection<ProgramCard>('program-cards', {
        populate: 'photo',
        sort: 'createdAt:asc',
      });
      set({ programCards });
    } catch (err) {
      console.error('Error fetching program cards:', err);
    } finally {
      set({ loading: false });
    }
  },

  createProgramCard: async ({ title, description, tab, cardColor, photo }) => {
    try {
      let photoId: number | null = null;
      if (photo) photoId = await uploadPhoto(photo);

      const payload: Record<string, unknown> = { title, description, tab, cardColor };
      if (photoId) payload.photo = photoId;

      const res = await fetch(`${baseUrl}/program-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });

      if (!res.ok) return null;
      const result = await res.json();
      await get().fetchProgramCards();
      return result.data ?? null;
    } catch (err) {
      console.error('Error creating program card:', err);
      return null;
    }
  },

  updateProgramCard: async (documentId, { title, description, tab, cardColor, photo, translationFields }) => {
    try {
      const payload: Record<string, unknown> = { title, description, tab, cardColor };
      // Spread only the touched translation fields; see the type comment above.
      if (translationFields) Object.assign(payload, translationFields);

      if (photo instanceof File) {
        // New file selected — upload and attach
        const photoId = await uploadPhoto(photo);
        if (photoId) payload.photo = photoId;
      } else if (photo === null) {
        // User explicitly removed the photo — clear the relation
        payload.photo = null;
      }
      // photo === undefined → keep existing, don't touch payload.photo

      const res = await fetch(`${baseUrl}/program-cards/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });

      if (!res.ok) return null;
      const result = await res.json();
      await get().fetchProgramCards();
      return result.data ?? null;
    } catch (err) {
      console.error('Error updating program card:', err);
      return null;
    }
  },

  deleteProgramCard: async (documentId) => {
    try {
      const res = await fetch(`${baseUrl}/program-cards/${documentId}`, { method: 'DELETE' });
      if (res.ok) {
        set((state) => ({
          programCards: state.programCards.filter((c) => c.documentId !== documentId),
        }));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error deleting program card:', err);
      return false;
    }
  },
}));
